import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  giftCouponStatisticsDataAtom,
  redemptionDashboardDataAtom,
  redemptionDashboardStatusAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  DeliverCouponDto,
  deliverCoupon,
  rejectCoupon,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  giftCouponStatisticsUrl,
  redemptionDashboardUrl,
  useGiftCouponStatisticsHook,
  useRedemptionDashboardHook,
} from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponForRedemption,
  GiftCouponForRedemptionDashboard,
  GiftCouponRedeemStatus,
  PhoneHistoryItem,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

const formatDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}:${min}`;
};

const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const historyStatusLabel: Record<string, { text: string; className: string }> = {
  [GiftCouponRedeemStatus.Delivered]: {
    text: '발송완료',
    className: 'bg-green-100 text-green-700',
  },
  [GiftCouponRedeemStatus.Expired]: {
    text: '만료',
    className: 'bg-gray-100 text-gray-600',
  },
  [GiftCouponRedeemStatus.Refunded]: {
    text: '환불',
    className: 'bg-red-100 text-red-700',
  },
  [GiftCouponRedeemStatus.Canceled]: {
    text: '취소',
    className: 'bg-gray-100 text-gray-500',
  },
  [GiftCouponRedeemStatus.Requested]: {
    text: '신청됨',
    className: 'bg-yellow-100 text-yellow-700',
  },
  [GiftCouponRedeemStatus.Rejected]: {
    text: '발송불가',
    className: 'bg-orange-100 text-orange-700',
  },
};

export const GiftCouponRedemptionListPage: FC = function GiftCouponRedemptionListPage() {
  useRedemptionDashboardHook();
  useGiftCouponStatisticsHook();

  const queryClient = useQueryClient();
  const dashboardStatus = useRecoilValue(redemptionDashboardStatusAtom);
  const dashboard = useRecoilValue(redemptionDashboardDataAtom);
  const statistics = useRecoilValue(giftCouponStatisticsDataAtom);

  const visibleRequested = useMemo(() => {
    const list = dashboard?.requested ?? [];
    return [...list].sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });
  }, [dashboard]);
  const todayDelivered = useMemo(() => {
    const list = dashboard?.todayDelivered ?? [];
    return [...list].sort((a, b) => {
      const dateA = a.gifticon?.deliveredAt
        ? new Date(a.gifticon.deliveredAt).getTime()
        : 0;
      const dateB = b.gifticon?.deliveredAt
        ? new Date(b.gifticon.deliveredAt).getTime()
        : 0;
      return dateB - dateA;
    });
  }, [dashboard]);

  const invalidateDashboard = useCallback(() => {
    queryClient.invalidateQueries(redemptionDashboardUrl);
    queryClient.invalidateQueries(giftCouponStatisticsUrl);
  }, [queryClient]);

  const [productFilter, setProductFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [deliveringId, setDeliveringId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [showTodayDelivered, setShowTodayDelivered] = useState(false);
  const [historyModal, setHistoryModal] = useState<{
    phone: string;
    history: PhoneHistoryItem[];
  } | null>(null);
  const [shareHistoryModal, setShareHistoryModal] = useState<{
    fullCode: string;
    history: Array<{ sharedAt: Date; unsharedAt?: Date }>;
  } | null>(null);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMessage(msg);
    toastTimer.current = setTimeout(() => setToastMessage(null), 1200);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  const [deliverModal, setDeliverModal] = useState<{
    couponIds: string[];
    priceRetail: number;
    priceWhole: string;
    priceRebate: string;
    isBulk: boolean;
  } | null>(null);

  const getChoiceUnitPriceRegular = useCallback(
    (couponId: string) => {
      const coupon = visibleRequested.find((c) => c._id === couponId);
      if (
        coupon?.selectedChoiceUnit &&
        typeof coupon.selectedChoiceUnit === 'object' &&
        coupon.selectedChoiceUnit.priceRegular
      ) {
        return coupon.selectedChoiceUnit.priceRegular;
      }
      if (typeof coupon?.giftItem === 'object') {
        return coupon.giftItem?.priceRegular || 0;
      }
      return 0;
    },
    [visibleRequested],
  );

  const openDeliverModal = useCallback(
    (couponId: string) => {
      const priceRetail = getChoiceUnitPriceRegular(couponId);
      setDeliverModal({
        couponIds: [couponId],
        priceRetail,
        priceWhole: '',
        priceRebate: '',
        isBulk: false,
      });
    },
    [getChoiceUnitPriceRegular],
  );

  const openBulkDeliverModal = useCallback(() => {
    const targetIds = Array.from(selectedIds);
    if (targetIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    const firstId = targetIds[0];
    if (!firstId) return;
    const priceRetail = getChoiceUnitPriceRegular(firstId);
    setDeliverModal({
      couponIds: targetIds,
      priceRetail,
      priceWhole: '',
      priceRebate: '',
      isBulk: true,
    });
  }, [selectedIds, getChoiceUnitPriceRegular]);

  const handleConfirmDeliver = useCallback(async () => {
    if (!deliverModal) return;

    const priceWhole = Number(deliverModal.priceWhole);
    const priceRebate = Number(deliverModal.priceRebate);

    if (!deliverModal.priceWhole || isNaN(priceWhole)) {
      alert('도매가를 입력해주세요.');
      return;
    }
    if (!deliverModal.priceRebate || isNaN(priceRebate)) {
      alert('리베이트를 입력해주세요.');
      return;
    }

    const dto: DeliverCouponDto = {
      priceWhole,
      priceRetail: deliverModal.priceRetail,
      priceRebate,
    };

    if (deliverModal.isBulk) {
      setIsBulkProcessing(true);
      setDeliverModal(null);
      let successCount = 0;
      let failCount = 0;

      for (const couponId of deliverModal.couponIds) {
        try {
          await deliverCoupon(couponId, dto);
          successCount++;
        } catch (error) {
          console.error(`발송 처리 실패 (${couponId}):`, error);
          failCount++;
        }
      }

      setIsBulkProcessing(false);
      setSelectedIds(new Set());
      invalidateDashboard();

      if (failCount === 0) {
        alert(`${successCount}건 발송 처리 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    } else {
      const couponId = deliverModal.couponIds[0] ?? '';
      setDeliveringId(couponId);
      setDeliverModal(null);

      try {
        await deliverCoupon(couponId, dto);
        invalidateDashboard();
        alert('발송 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송 처리 실패:', error);
        alert('발송 처리에 실패했습니다.');
      } finally {
        setDeliveringId(null);
      }
    }
  }, [deliverModal, invalidateDashboard]);

  const handleReject = useCallback(
    async (couponId: string) => {
      if (
        !confirm('이 쿠폰을 발송불가 처리하시겠습니까?\n(수신자 번호가 잘못된 경우 등)')
      ) {
        return;
      }
      try {
        await rejectCoupon(couponId);
        invalidateDashboard();
        alert('발송불가 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송불가 처리 실패:', error);
        alert('발송불가 처리에 실패했습니다.');
      }
    },
    [invalidateDashboard],
  );

  const handleToggleSelect = useCallback((couponId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(couponId)) {
        newSet.delete(couponId);
      } else {
        newSet.add(couponId);
      }
      return newSet;
    });
  }, []);

  const productOptions = useMemo(() => {
    const optionMap = new Map<string, { key: string; label: string; count: number }>();

    for (const coupon of visibleRequested) {
      let key: string;
      let label: string;

      if (
        coupon.selectedChoiceUnit &&
        typeof coupon.selectedChoiceUnit === 'object' &&
        coupon.selectedChoiceUnit._id
      ) {
        key = `unit:${coupon.selectedChoiceUnit._id}`;
        const itemName =
          typeof coupon.giftItem === 'object'
            ? coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || ''
            : '';
        label = itemName
          ? `${itemName} - ${coupon.selectedChoiceUnit.name}`
          : coupon.selectedChoiceUnit.name;
      } else if (typeof coupon.giftItem === 'object' && coupon.giftItem?._id) {
        key = `item:${coupon.giftItem._id}`;
        label = coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
      } else {
        continue;
      }

      const existing = optionMap.get(key);
      if (existing) {
        existing.count++;
      } else {
        optionMap.set(key, { key, label, count: 1 });
      }
    }

    return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [visibleRequested]);

  const filteredData = useMemo(() => {
    let result = [...visibleRequested];

    if (productFilter !== 'all') {
      result = result.filter((item) => {
        if (productFilter.startsWith('unit:')) {
          const unitId = productFilter.slice(5);
          return (
            item.selectedChoiceUnit &&
            typeof item.selectedChoiceUnit === 'object' &&
            item.selectedChoiceUnit._id === unitId
          );
        } else if (productFilter.startsWith('item:')) {
          const itemId = productFilter.slice(5);
          return typeof item.giftItem === 'object' && item.giftItem?._id === itemId;
        }
        return true;
      });
    }

    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((item) => {
        const phone = item.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const itemName =
          typeof item.giftItem === 'object'
            ? (
                item.giftItem?.name ||
                item.giftItem?.giftItemInfo?.name ||
                ''
              ).toLowerCase()
            : '';
        const unitName =
          item.selectedChoiceUnit && typeof item.selectedChoiceUnit === 'object'
            ? item.selectedChoiceUnit.name?.toLowerCase() || ''
            : '';
        const fullCode = (item.fullCode || '').toLowerCase();
        const order = typeof item.giftOrder === 'object' ? item.giftOrder : null;
        const ordererPhone = (order?.bugo?.member?.phoneNumber || '').replace(/-/g, '');
        return (
          phone.includes(search) ||
          itemName.includes(search) ||
          unitName.includes(search) ||
          fullCode.includes(search) ||
          ordererPhone.includes(search)
        );
      });
    }

    result.sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });

    return result;
  }, [visibleRequested, productFilter, searchText]);

  const isAllSelected = useMemo(
    () =>
      filteredData.length > 0 && filteredData.every((item) => selectedIds.has(item._id)),
    [filteredData, selectedIds],
  );

  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map((item) => item._id)));
    }
  }, [isAllSelected, filteredData]);

  const formatPhone = useCallback((phone: string | undefined) => {
    if (!phone) return '-';
    const numbers = phone.replace(/[^0-9]/g, '');
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return phone;
  }, []);

  const getOrdererInfo = useCallback((coupon: GiftCouponForRedemption) => {
    const order = typeof coupon.giftOrder === 'object' ? coupon.giftOrder : null;
    if (!order) return { name: '-', phone: '-', purchasedAt: undefined };
    return {
      name: order.bugo?.member?.fullName || '-',
      phone: order.bugo?.member?.phoneNumber || '-',
      purchasedAt: order.paymentDetail?.purchasedAt,
    };
  }, []);

  const getItemName = useCallback((coupon: GiftCouponForRedemption) => {
    if (typeof coupon.giftItem === 'object') {
      return coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
    }
    return '-';
  }, []);

  const getChoiceUnitName = useCallback((coupon: GiftCouponForRedemption) => {
    if (coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object') {
      return coupon.selectedChoiceUnit.name;
    }
    return null;
  }, []);

  const getPriceInfo = useCallback((coupon: GiftCouponForRedemption) => {
    if (typeof coupon.giftItem === 'object') {
      return {
        priceRegular: coupon.giftItem?.priceRegular || 0,
        priceRetail: coupon.giftItem?.priceRetail || 0,
        priceWhole: coupon.giftItem?.priceWhole || 0,
        priceRebate: coupon.giftItem?.priceRebate || 0,
      };
    }
    return { priceRegular: 0, priceRetail: 0, priceWhole: 0, priceRebate: 0 };
  }, []);

  const getImageUrl = useCallback((coupon: GiftCouponForRedemption) => {
    if (
      coupon.selectedChoiceUnit &&
      typeof coupon.selectedChoiceUnit === 'object' &&
      coupon.selectedChoiceUnit.image
    ) {
      const img = coupon.selectedChoiceUnit.image;
      const imgUrl = typeof img === 'object' && 'url' in img ? img.url : img;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
    if (typeof coupon.giftItem === 'object') {
      const giftItemImg = coupon.giftItem?.image;
      if (giftItemImg) {
        const imgUrl =
          typeof giftItemImg === 'object' && 'url' in giftItemImg
            ? giftItemImg.url
            : giftItemImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
      const giftItemInfoImg = coupon.giftItem?.giftItemInfo?.image;
      if (giftItemInfoImg) {
        const imgUrl =
          typeof giftItemInfoImg === 'object' && 'url' in giftItemInfoImg
            ? giftItemInfoImg.url
            : giftItemInfoImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
    }
    return '';
  }, []);

  if (dashboardStatus === 'loading' || dashboardStatus === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">발송 대기 쿠폰</h1>
            <p className="mt-1 text-sm text-gray-500">
              교환 신청된 쿠폰을 확인하고 기프티콘을 발송하세요.
            </p>
          </div>
          <Link
            to="/gift-seller/coupon-history"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            쿠폰 내역 검색
          </Link>
        </div>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-6 gap-3 p-4">
        <div className="rounded-lg bg-yellow-50 p-3 text-center">
          <p className="text-xl font-bold text-yellow-700">
            {statistics?.pending ?? visibleRequested.length}
          </p>
          <p className="text-xs text-yellow-600">발송 대기</p>
        </div>
        <div className="rounded-lg bg-green-50 p-3 text-center">
          <p className="text-xl font-bold text-green-700">
            {statistics?.delivered ?? '-'}
          </p>
          <p className="text-xs text-green-600">발송 완료</p>
        </div>
        <div className="rounded-lg bg-gray-100 p-3 text-center">
          <p className="text-xl font-bold text-gray-700">{statistics?.total ?? '-'}</p>
          <p className="text-xs text-gray-600">전체</p>
        </div>
        <div className="rounded-lg bg-red-50 p-3 text-center">
          <p
            className={`text-xl font-bold ${
              statistics?.blacklist && statistics.blacklist.length > 0
                ? 'text-red-700'
                : 'text-gray-400'
            }`}
          >
            {statistics?.blacklist?.length ?? 0}
          </p>
          <p className="text-xs text-red-600">중복 의심</p>
        </div>
        <div className="rounded-lg bg-orange-50 p-3 text-center">
          <p
            className={`text-xl font-bold ${
              statistics?.rejected ? 'text-orange-700' : 'text-gray-400'
            }`}
          >
            {statistics?.rejected ?? 0}
          </p>
          <p className="text-xs text-orange-600">발송불가</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 text-center">
          <p
            className={`text-xl font-bold ${
              statistics?.canceled ? 'text-gray-700' : 'text-gray-400'
            }`}
          >
            {statistics?.canceled ?? 0}
          </p>
          <p className="text-xs text-gray-500">취소</p>
        </div>
      </div>

      {/* 오늘 발송 내역 토글 */}
      {todayDelivered.length > 0 && (
        <div className="px-4 pb-2">
          <button
            onClick={() => setShowTodayDelivered((prev) => !prev)}
            className="flex w-full items-center justify-between rounded-lg bg-green-50 px-4 py-3 text-left transition-colors hover:bg-green-100"
          >
            <span className="text-sm font-medium text-green-800">
              오늘 발송 완료 ({todayDelivered.length}건)
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 text-green-600 transition-transform ${
                showTodayDelivered ? 'rotate-180' : ''
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showTodayDelivered &&
            (() => {
              const groups: {
                key: string;
                label: string;
                items: typeof todayDelivered;
              }[] = [];
              for (const coupon of todayDelivered) {
                const dt = coupon.gifticon?.deliveredAt;
                const key = dt ? formatDateTime(dt) : '-';
                const last = groups[groups.length - 1];
                if (last && last.key === key) {
                  last.items.push(coupon);
                } else {
                  groups.push({ key, label: key, items: [coupon] });
                }
              }

              return (
                <div className="mt-2 overflow-x-auto rounded-lg bg-white shadow">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-green-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                          쿠폰번호
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                          _id
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                          상품
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                          수신자
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-green-700">
                          도매가
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                          신청시각
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                          발송시각
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((group, groupIdx) => (
                        <Fragment key={group.key + groupIdx}>
                          {groupIdx > 0 && (
                            <tr>
                              <td colSpan={7} className="h-0 p-0">
                                <div className="border-t-2 border-green-200" />
                              </td>
                            </tr>
                          )}
                          {group.items.length > 1 && (
                            <tr className="bg-green-50/60">
                              <td
                                colSpan={7}
                                className="px-4 py-1.5 text-xs font-semibold text-green-700"
                              >
                                {group.label} 발송 · {group.items.length}건
                              </td>
                            </tr>
                          )}
                          {group.items.map((coupon) => (
                            <tr
                              key={coupon._id}
                              className="border-t border-gray-100 hover:bg-gray-50"
                            >
                              <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-700">
                                {coupon.fullCode || '-'}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2">
                                <button
                                  onClick={() => copyToClipboard(coupon._id)}
                                  className="font-mono text-xs text-gray-400 hover:text-indigo-600"
                                  title="클릭하여 _id 복사"
                                >
                                  {coupon._id.slice(-6)}
                                </button>
                              </td>
                              <td className="px-4 py-2 text-xs text-gray-900">
                                {getItemName(coupon)}
                                {getChoiceUnitName(coupon) && (
                                  <span className="ml-1 text-indigo-500">
                                    ({getChoiceUnitName(coupon)})
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-700">
                                {formatPhone(coupon.recipient?.phoneNumber)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-indigo-600">
                                {coupon.gifticon?.priceWhole != null
                                  ? `${coupon.gifticon.priceWhole.toLocaleString()}원`
                                  : '-'}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                                {formatDateTime(coupon.recipient?.requestedAt)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                                {formatDateTime(coupon.gifticon?.deliveredAt)}
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="max-w-[260px] rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상품</option>
            {productOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label} ({opt.count})
              </option>
            ))}
          </select>

          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="쿠폰번호, 전화번호, 주문자번호, 상품명 검색"
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        {selectedIds.size > 0 && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-indigo-50 px-4 py-3">
            <span className="text-sm font-medium text-indigo-700">
              {selectedIds.size}건 선택됨
            </span>
            <button
              onClick={openBulkDeliverModal}
              disabled={isBulkProcessing}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBulkProcessing ? '처리 중...' : '일괄 발송처리'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              선택 해제
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="p-4">
        {filteredData.length > 0 ? (
          <div className="overflow-x-auto rounded-lg bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleToggleAll}
                      disabled={filteredData.length === 0}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    쿠폰번호
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    _id
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    상품
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    정가
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    판매가
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    도매가
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    주문자
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    구매일
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <div className="flex items-center gap-1.5">
                      수신자 정보
                      {productFilter !== 'all' && (
                        <button
                          onClick={() => {
                            const phones = filteredData
                              .map((c) => c.recipient?.phoneNumber)
                              .filter((p): p is string => !!p)
                              .map((p) => p.replace(/[^0-9]/g, ''))
                              .filter((p) => p.length > 0);
                            if (phones.length === 0) {
                              showToast('복사할 번호가 없습니다.');
                              return;
                            }
                            copyToClipboard(phones.join('\n'));
                          }}
                          title={`필터된 수신자 번호 전체 복사 (${filteredData.length}건)`}
                          className="inline-flex items-center gap-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-indigo-700 hover:bg-indigo-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                          </svg>
                          번호 복사 ({filteredData.length})
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    신청일시
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    만료일
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredData.map((coupon) => {
                  const choiceUnitName = getChoiceUnitName(coupon);
                  const priceInfo = getPriceInfo(coupon);
                  const ordererInfo = getOrdererInfo(coupon);
                  const isSelected = selectedIds.has(coupon._id);
                  const isDuplicate = (coupon as GiftCouponForRedemptionDashboard)
                    .isDuplicatePhone;

                  return (
                    <tr
                      key={coupon._id}
                      className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''} ${
                        isDuplicate ? 'border-l-4 border-l-red-400' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(coupon._id)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {coupon.shareHistory && coupon.shareHistory.length > 0 && (
                            <button
                              onClick={() =>
                                setShareHistoryModal({
                                  fullCode: coupon.fullCode || coupon._id,
                                  history: coupon.shareHistory ?? [],
                                })
                              }
                              className="inline-flex items-center rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700 hover:bg-purple-200"
                              title="공유 이력 보기"
                            >
                              {coupon.shareHistory.length}
                            </button>
                          )}
                          <span className="font-mono text-sm text-gray-700">
                            {coupon.fullCode || '-'}
                          </span>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <button
                          onClick={() => copyToClipboard(coupon._id)}
                          className="font-mono text-xs text-gray-400 hover:text-indigo-600"
                          title="클릭하여 _id 복사"
                        >
                          {coupon._id.slice(-6)}
                        </button>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {getImageUrl(coupon) && (
                            <img
                              src={getImageUrl(coupon)}
                              alt=""
                              className="h-10 w-10 rounded object-cover"
                            />
                          )}
                          <div>
                            <div className="flex items-center gap-1">
                              <p className="text-sm font-medium text-gray-900">
                                {getItemName(coupon)}
                              </p>
                              {choiceUnitName &&
                                (coupon.selectedChoiceUnit &&
                                typeof coupon.selectedChoiceUnit === 'object' &&
                                coupon.selectedChoiceUnit.wholeUrl ? (
                                  <a
                                    href={coupon.selectedChoiceUnit.wholeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`도매사이트 이동: ${choiceUnitName}`}
                                    className="shrink-0 rounded p-0.5 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-3.5 w-3.5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                                      <polyline points="15 3 21 3 21 9" />
                                      <line x1="10" y1="14" x2="21" y2="3" />
                                    </svg>
                                  </a>
                                ) : (
                                  <button
                                    onClick={() => copyToClipboard(choiceUnitName)}
                                    title={choiceUnitName}
                                    className="shrink-0 rounded p-0.5 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-3.5 w-3.5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <rect
                                        x="9"
                                        y="9"
                                        width="13"
                                        height="13"
                                        rx="2"
                                        ry="2"
                                      />
                                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                    </svg>
                                  </button>
                                ))}
                            </div>
                            {choiceUnitName && (
                              <p className="max-w-[200px] text-xs text-indigo-600">
                                {choiceUnitName}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-500">
                        {priceInfo.priceRegular.toLocaleString()}원
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {priceInfo.priceRetail.toLocaleString()}원
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-indigo-600">
                        {priceInfo.priceWhole.toLocaleString()}원
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {ordererInfo.name}
                        </p>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(ordererInfo.purchasedAt)}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => {
                              const phone = coupon.recipient?.phoneNumber;
                              if (phone) copyToClipboard(phone);
                            }}
                            className="text-sm font-medium text-gray-900 hover:text-indigo-600"
                            title="클릭하여 복사"
                          >
                            {formatPhone(coupon.recipient?.phoneNumber)}
                          </button>
                          {isDuplicate && (
                            <button
                              onClick={() => {
                                const dc = coupon as GiftCouponForRedemptionDashboard;
                                if (dc.phoneHistory && dc.phoneHistory.length > 0) {
                                  setHistoryModal({
                                    phone: coupon.recipient?.phoneNumber || '',
                                    history: dc.phoneHistory,
                                  });
                                }
                              }}
                              className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700 hover:bg-red-200"
                              title="클릭하여 과거 이력 확인"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              중복
                            </button>
                          )}
                        </div>
                        {coupon.recipient?.name && (
                          <p className="text-xs text-gray-500">{coupon.recipient.name}</p>
                        )}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDateTime(coupon.recipient?.requestedAt)}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(coupon.expiresAt)}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <button
                          onClick={() => openDeliverModal(coupon._id)}
                          disabled={deliveringId === coupon._id}
                          className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {deliveringId === coupon._id ? '처리중...' : '발송처리'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg bg-white p-8 text-center shadow">
            <p className="text-gray-500">발송 대기 중인 쿠폰이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 발송처리 모달 */}
      {deliverModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setDeliverModal(null)}
          />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900">
              발송 처리
              {deliverModal.isBulk ? ` (${deliverModal.couponIds.length}건)` : ''}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              가격 정보를 입력한 후 발송 처리하세요.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  판매가 (priceRetail)
                </label>
                <div className="mt-1 flex items-center rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  {deliverModal.priceRetail.toLocaleString()}원
                  <span className="ml-2 text-xs text-gray-400">(자동)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  도매가 (priceWhole)
                </label>
                <input
                  type="number"
                  value={deliverModal.priceWhole}
                  onChange={(e) =>
                    setDeliverModal((prev) =>
                      prev ? { ...prev, priceWhole: e.target.value } : null,
                    )
                  }
                  placeholder="도매가 입력"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  리베이트 (priceRebate)
                </label>
                <input
                  type="number"
                  value={deliverModal.priceRebate}
                  onChange={(e) =>
                    setDeliverModal((prev) =>
                      prev ? { ...prev, priceRebate: e.target.value } : null,
                    )
                  }
                  placeholder="리베이트 입력"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              {!deliverModal.isBulk ? (
                <button
                  onClick={() => {
                    const couponId = deliverModal.couponIds[0] ?? '';
                    setDeliverModal(null);
                    handleReject(couponId);
                  }}
                  className="text-xs text-orange-600 hover:text-orange-800 hover:underline"
                >
                  발송불가 처리
                </button>
              ) : (
                <div />
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setDeliverModal(null)}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={handleConfirmDeliver}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  발송 처리
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 과거 교환 이력 모달 */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setHistoryModal(null)}
          />
          <div className="relative max-h-[80vh] w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="border-b bg-red-50 px-6 py-4">
              <div className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-red-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <h3 className="text-lg font-bold text-red-800">
                  과거 교환 이력 - {formatPhone(historyModal.phone)}
                </h3>
              </div>
              <p className="mt-1 text-sm text-red-600">
                이 번호로 과거{' '}
                <span className="font-bold">{historyModal.history.length}건</span>의 교환
                이력이 있습니다.
              </p>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
              <div className="space-y-3">
                {historyModal.history.map((item, idx) => {
                  const badge = historyStatusLabel[item.redeemStatus] ?? {
                    text: item.redeemStatus,
                    className: 'bg-gray-100 text-gray-600',
                  };
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3"
                    >
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${badge.className}`}
                      >
                        {badge.text}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">
                          {item.selectedChoiceUnit?.name || '-'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(item.requestedAt)} · {item.ordererName} 주문
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="border-t px-6 py-3">
              <button
                onClick={() => setHistoryModal(null)}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 공유 이력 모달 */}
      {shareHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShareHistoryModal(null)}
          />
          <div className="relative max-h-[80vh] w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="border-b bg-purple-50 px-6 py-4">
              <h3 className="text-lg font-bold text-purple-800">
                공유 이력 - {shareHistoryModal.fullCode}
              </h3>
              <p className="mt-1 text-sm text-purple-600">
                총 <span className="font-bold">{shareHistoryModal.history.length}회</span>{' '}
                공유
              </p>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      #
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      공유 시각
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      공유 해제 시각
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shareHistoryModal.history.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                        {idx + 1}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-900">
                        {formatDateTime(item.sharedAt)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-900">
                        {item.unsharedAt ? formatDateTime(item.unsharedAt) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t px-6 py-3">
              <button
                onClick={() => setShareHistoryModal(null)}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toastMessage && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[100] flex justify-center">
          <div className="animate-fade-in-out rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {toastMessage}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          15% { opacity: 1; transform: translateY(0); }
          75% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        .animate-fade-in-out {
          animation: fadeInOut 1.2s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
};
